/**
 * Hook simplifié pour le formulaire candidat
 * Remplace useSubmitReferents complexe par une approche unifiée
 * Élimine la race condition en chargeant toutes les données d'un coup
 */

import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { relationTypes, type SubmitReferentsFormData } from '@/types/referents';
import { type Campaign } from '@/types/unified';
import { campaignService, type CandidateResponseStatus } from '@/services/campaignService';
import { referentService } from '@/services/referentService';
import { ErrorHandler } from '@/services/ErrorHandler';
import { logger } from '@/utils/productionLogger';

// Interface pour les données unifiées
interface CandidateFormData {
  campaign: Campaign;
  candidateStatus: CandidateResponseStatus;
  branding: {
    enabled: boolean;
    logoUrl?: string | null;
    primaryColor?: string;
    accentColor?: string;
    secondaryColor?: string;
  };
}

export const useCandidateForm = (token: string | undefined) => {
  const { toast } = useToast();
  const [data, setData] = useState<CandidateFormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Fonction de chargement unifiée
  const loadFormData = useCallback(async () => {
    if (!token) {
      setError('Token manquant dans l\'URL. Vérifiez que le lien est complet.');
      setLoading(false);
      return;
    }

    try {
      setError(null);
      logger.debug('🚀 Chargement unifié des données formulaire candidat', { token });

      // NOUVEAU: Un seul appel RPC au lieu de 4 appels séparés
      const formData = await campaignService.getCandidateFormDataDirect(token);

      // Transformer les données pour correspondre aux interfaces existantes
      const unifiedData: CandidateFormData = {
        campaign: formData.campaign,
        candidateStatus: formData.candidateStatus,
        branding: formData.branding
      };

      setData(unifiedData);
      logger.debug('✅ Données formulaire candidat chargées en une seule requête', unifiedData);

    } catch (error: any) {
      logger.error('Erreur lors du chargement unifié', error);
      const handledError = ErrorHandler.handle(error);
      setError(handledError.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fonction d'acceptation optimisée
  const handleAccept = async () => {
    if (!data?.campaign || !token) {
      logger.error('Pas de données campagne pour acceptation');
      return;
    }

    setIsSubmitting(true);

    try {
      logger.debug('Début acceptation campagne avec token', { token });
      await campaignService.acceptCampaign(token);
      
      // OPTIMISATION: Mise à jour optimiste de l'état local au lieu de refresh complet
      setData(prev => prev ? {
        ...prev,
        candidateStatus: {
          ...prev.candidateStatus,
          status: 'accepted',
          response_type: 'accept'
        }
      } : null);
      
      // Scroll vers le haut après acceptation
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      logger.info('Acceptation réussie avec mise à jour optimiste');
    } catch (error: any) {
      logger.error('Erreur lors de l\'acceptation', error);
      const handledError = ErrorHandler.handle(error);
      toast({
        title: "Erreur",
        description: handledError.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fonction de refus optimisée
  const handleRefuse = async (reason: string) => {
    if (!data?.campaign || !token) {
      logger.error('Pas de données campagne pour refus');
      return;
    }

    setIsSubmitting(true);

    try {
      logger.debug('Début refus campagne avec token', { token });
      await campaignService.refuseCampaign(token, reason);

      toast({
        title: "Refus enregistré",
        description: "Le recruteur a été informé de votre décision.",
      });

      // OPTIMISATION: Mise à jour optimiste de l'état local
      setData(prev => prev ? {
        ...prev,
        candidateStatus: {
          ...prev.candidateStatus,
          status: 'refused',
          response_type: 'refuse',
          refusal_reason: reason
        }
      } : null);
      
      // Scroll vers le haut après refus
      window.scrollTo({ top: 0, behavior: 'smooth' });

      logger.info('Refus enregistré avec mise à jour optimiste');
    } catch (error: any) {
      logger.error('Erreur lors du refus', error);
      const handledError = ErrorHandler.handle(error);
      toast({
        title: "Erreur",
        description: handledError.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fonction de soumission optimisée
  const handleSubmit = async (formData: SubmitReferentsFormData) => {
    if (!data?.campaign || !token) {
      logger.error('Pas de données campagne pour soumission');
      return;
    }

    setIsSubmitting(true);

    try {
      logger.debug('Début soumission référents pour token', { token });
      logger.debug('Données référents', { referents: formData.referents });
      
      const { emailSuccess } = await referentService.submitReferents(token, formData);

      if (emailSuccess) {
        toast({
          title: "Référents soumis avec succès",
          description: "Les référents ont été contactés et recevront un email sous peu.",
        });
      } else {
        toast({
          title: "Référents soumis avec avertissement",
          description: "Les référents ont été enregistrés mais l'envoi des emails a échoué.",
          variant: "default",
        });
      }

      setIsSubmitted(true);
      
      // OPTIMISATION: Mise à jour optimiste au lieu de refresh complet
      setData(prev => prev ? {
        ...prev,
        candidateStatus: {
          ...prev.candidateStatus,
          status: 'completed'
        }
      } : null);
      
      // Scroll vers le haut après soumission réussie
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      logger.info('Soumission référents réussie avec mise à jour optimiste');
    } catch (error: any) {
      logger.error('Erreur lors de la soumission des référents', error);
      const handledError = ErrorHandler.handle(error);
      toast({
        title: "Erreur",
        description: handledError.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Chargement initial
  useEffect(() => {
    loadFormData();
  }, [loadFormData]);

  // Valeurs dérivées pour compatibilité avec l'interface existante
  const campaign = data?.campaign || null;
  const candidateStatus = data?.candidateStatus || null;
  const branding = data?.branding || null;

  return {
    // Données
    campaign,
    candidateStatus,
    branding,
    
    // États
    loading,
    error,
    isSubmitting,
    isSubmitted,
    
    // États dérivés
    isRefused: candidateStatus?.status === 'refused',
    showDecisionForm: candidateStatus?.status === 'no_response',
    
    // Actions
    handleAccept,
    handleRefuse,
    onSubmit: handleSubmit,
    
    // Compatibilité
    relationTypes,
    
    // Fonction de rechargement (si nécessaire)
    refresh: loadFormData
  };
};